// Mirror of Shared/Calculators/BaseboardCalculator.cs.
import type { CalcDef, ResultLine } from "../types.js";
import * as F from "../format.js";

const fields: CalcDef["fields"] = [
  { key: "roomWidthFt", kind: "number", default: 12, min: 1, max: 200 },
  { key: "roomLengthFt", kind: "number", default: 14, min: 1, max: 200 },
  { key: "doors", kind: "number", default: 1, min: 0, max: 20 },
  {
    key: "pieceLength",
    kind: "select",
    defaultChoice: "12",
    choices: [
      { value: "8", set: { pieceLengthFt: 8 } },
      { value: "12", set: { pieceLengthFt: 12 } },
      { value: "16", set: { pieceLengthFt: 16 } },
    ],
  },
  { key: "wastePct", kind: "number", default: 10, min: 0, max: 25 },
  { key: "pricePerFt", kind: "number", default: 2, min: 0, max: 50 },
];

function compute(i: Record<string, number>): Record<string, number> {
  const w = F.get(i, "roomWidthFt");
  const l = F.get(i, "roomLengthFt");
  const doors = F.get(i, "doors");
  const pieceLen = F.get(i, "pieceLengthFt");
  const waste = F.get(i, "wastePct");
  const price = F.get(i, "pricePerFt");

  const perimeter = 2 * (w + l);
  const linearFt = Math.max(0, perimeter - doors * 3);
  const withWaste = linearFt * (1 + waste / 100);
  const pieces = pieceLen > 0 ? F.ceilUnits(withWaste / pieceLen) : 0;
  const cost = pieces * pieceLen * price;

  return {
    perimeter: F.round(perimeter, 2),
    linearFt: F.round(linearFt, 2),
    withWaste: F.round(withWaste, 2),
    pieces,
    cost: F.round(cost, 2),
  };
}

function present(_i: Record<string, number>, r: Record<string, number>): ResultLine[] {
  const pieces = F.get(r, "pieces");
  return [
    { label: "Perimeter", value: `${F.dec(F.get(r, "perimeter"))} ft` },
    { label: "Trim needed (with waste)", value: `${F.dec(F.get(r, "withWaste"))} ft` },
    { label: "Pieces to buy", value: `${F.intStr(pieces)} ${F.plural(pieces, "piece", "pieces")}`, highlight: true },
    { label: "Est. materials cost", value: F.usd(F.get(r, "cost")), sub: "rough — trim only, excludes nails, caulk, paint & labor" },
  ];
}

export const baseboard: CalcDef = { slug: "baseboard-calculator", fields, compute, present };
